/* eslint-disable react/jsx-max-depth */

/**
 * Libs
 */

/**
 * Styles
 */
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Constants
 */
import { appLinks } from 'src/constants/common.constants';
import { FaqCategoriesMenu } from 'src/features/common/faq/components/faq-categories-menu';
import { FaqPageSection } from 'src/features/common/faq/components/faq-page-section';
import { FAQ_ITEMS } from 'src/features/common/faq/containers/constants';
import { StyledFaqPageContent, StyledImg, StyledList, StyledListStep, StyledParagraph } from 'src/features/common/faq/containers/faq-container.styles';
import { ILocale } from 'src/features/common/faq/containers/highlighted-questions/types';

/**
 * Hooks
 */
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { StyledImageContainer } from 'src/features/white-labels/ocean/faq/containers/faq-container.styles';
import { TechPageHeader } from 'src/features/white-labels/worldofartists/tech-pages/components/tech-page-header';
import { useWindowListener } from 'src/hooks/use-window-listener';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { PageLayout } from 'src/ui/common/layouts/page-layout';

/**
 * Components
 */
import { Visible } from 'src/ui/common/layouts/visible';
import { Accordion } from 'src/ui/common/molecules/accordion';


export const categories = [
    { title: 'General information', anchor: 'general-information' },
    { title: 'Buying', anchor: 'buying' },
    { title: 'Selling', anchor: 'selling' },
    { title: 'Wallet', anchor: 'wallet' },
    { title: 'Integration information', anchor: 'integration' },
];

export const FaqContainer = () => {
    const { t } = useTranslation(['pages']);
    const { asPath } = useRouter();
    const { projectName } = useUniverse();
    const isMounted = useIsMounted();
    const [activeSection, setActiveSection] = useState<string>(categories[0].anchor);
    const belowElementsRef = useRef<Element[]>();

    const locale: ILocale = useMemo(
        () => ({
            ...t('faq', { returnObjects: true, faqCompanyName: projectName }),
        }),
        [t, projectName]
    );

    const [, initialOpenedId] = useMemo(
        () => asPath.split('#'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useWindowListener(
        'scroll',
        () => {
            const below = belowElementsRef.current?.reduce<Element[]>((acc, curr) => {
                if (curr.getBoundingClientRect().top > 0) {
                    acc.push(curr);
                }

                return acc;
            }, []);

            if (below?.length) {
                setActiveSection(below[0].id);
            }
        },
        100
    );

    useEffect(() => {
        isMounted(() => {
            belowElementsRef.current = [...document.querySelectorAll('[data-observablesection]')];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <TechPageHeader
                title={`${projectName} FAQ`}
                subtitle="Search our knowledge base or browse categories below."
                type="faq"
            />
            <PageLayout fullWidth={false}>
                <StyledFaqPageContent>
                    <Visible view="desktop">
                        <FaqCategoriesMenu active={activeSection} categories={categories} />
                    </Visible>
                    <FlexBox flexDirection="column">
                        <Accordion initialOpenedId={initialOpenedId}>
                            <FaqPageSection title="General information" anchor="general-information">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                                    {locale?.whatIsCompany?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                                    <StyledParagraph>
                                        WorldofartistsNFT allows users to transform digital content into NFTs residing
                                        on the blockchain. The digital content could include books, images, videos,
                                        musical tracks, or even complex digital files. Users can just enter the details
                                        for describing their item and use the blockchain for taking their digital item
                                        to the public. Users could browse the WorldofartistsNFT marketplace for making
                                        bids and purchasing digital items. The items reside on the blockchain and are
                                        stored in your wallet when you purchase them.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_START}>
                                    {locale?.howToStart?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_START}>
                                    <Typography semiBold>For contend creators:</Typography>
                                    <StyledList>
                                        <li>
                                            You can contact us through our email{' '}
                                            <a href="mailto:info@worldofartistsnft.com">info@worldofartistsnft.com</a>
                                        </li>
                                        <li>
                                            You can email directly to Tim Alvin Boström if you have something specific
                                            you want to discuss:{' '}
                                            <a href="mailto:Tim@worldofartistsnft.com">Tim@worldofartistsnft.com</a>
                                        </li>
                                    </StyledList>
                                    <Typography semiBold>For buyers of NFTs: </Typography>
                                    <StyledList>
                                        <li>Simply follow the link provided by your favorite artist.</li>
                                        <li>
                                            If you have any questions, you can contact us through our email:{' '}
                                            <a href="mailto:support@worldofartistsnft.com">
                                                support@worldofartistsnft.com
                                            </a>
                                        </li>
                                    </StyledList>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                                    {locale?.whatIsNFT?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                                    <Spacing pr="20px">
                                        {locale?.whatIsNFT?.paragraphs?.map((item) => (
                                            <StyledParagraph key={item}>{item}</StyledParagraph>
                                        ))}
                                        <StyledList>
                                            {locale?.whatIsNFT?.fungibleItems?.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </StyledList>
                                        <StyledParagraph>{locale?.whatIsNFT?.nonFungible}</StyledParagraph>
                                        <StyledList>
                                            {locale?.whatIsNFT?.nonFungibleItems?.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </StyledList>
                                        <StyledParagraph>{locale?.whatIsNFT?.publiclyOwnership}</StyledParagraph>
                                        <StyledList>
                                            {locale?.whatIsNFT?.publiclyOwnershipItems?.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </StyledList>
                                    </Spacing>
                                </Accordion.ItemContent>

                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_SC}>
                                    {locale?.whatIsSmartContract?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_SC}>
                                    {locale?.whatIsSmartContract?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Buying" anchor="buying">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_USE_FOR_PAYMENT}>
                                    {locale?.paymentWhenBuying?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_USE_FOR_PAYMENT}>
                                    <StyledParagraph>{locale?.paymentWhenBuying?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Selling" anchor="selling">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                                    {locale?.whatIsMinting?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                                    {locale?.whatIsMinting?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.ARE_NFTS_REFUNDABLE}>
                                    {locale?.areNftRefundable?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.ARE_NFTS_REFUNDABLE}>
                                    <StyledParagraph>{locale?.areNftRefundable?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_FIND_NFT}>
                                    {locale?.whereDoIFindNFT?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_FIND_NFT}>
                                    <StyledParagraph>{locale?.whereDoIFindNFT?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.ROYALTIES}>
                                    {locale?.howDoRoyaltiesWork?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.ROYALTIES}>
                                    {locale?.howDoRoyaltiesWork?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.CAP_OF_ROYALTIES}>
                                    {locale?.capOfRoyalties?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.CAP_OF_ROYALTIES}>
                                    <StyledParagraph>{locale?.capOfRoyalties?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Wallet" anchor="wallet">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_WALLET_IS_USED}>
                                    {locale?.wallets?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_WALLET_IS_USED}>
                                    <StyledParagraph>{locale?.wallets?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.OTHER_WALLET}>
                                    {locale?.otherWallets?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.OTHER_WALLET}>
                                    <StyledParagraph>{locale?.otherWallets?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_CAN_I_DOWNLOAD_WALLETS}>
                                    {locale?.downloadWallets?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_CAN_I_DOWNLOAD_WALLETS}>
                                    <StyledParagraph>{locale?.downloadWallets?.cryptoX}</StyledParagraph>
                                    <Spacing pl="20px">
                                        <StyledParagraph>{locale?.downloadWallets?.clickLinks}</StyledParagraph>
                                        <Typography tag="p">
                                            {locale?.downloadWallets?.cryptoIOS}
                                            <a href={appLinks.appStore} target="_blank" rel="noreferrer">
                                                {appLinks.appStore}
                                            </a>
                                            <br />
                                            {locale?.downloadWallets?.cryptoAndroid}
                                            <a href={appLinks.googlePlay} target="_blank" rel="noreferrer">
                                                {appLinks.googlePlay}
                                            </a>
                                        </Typography>
                                    </Spacing>
                                    <StyledParagraph>{locale?.downloadWallets?.metaMask}</StyledParagraph>
                                    <Spacing pl="20px">
                                        <StyledParagraph>{locale?.downloadWallets?.clickLink}</StyledParagraph>
                                        <Typography tag="p">
                                            <a href={appLinks.metamaskInstall} target="_blank" rel="noreferrer">
                                                {appLinks.metamaskInstall}
                                            </a>
                                        </Typography>
                                    </Spacing>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_CONNECT_CRYPTOX}>
                                    {locale?.connectUseCryptoX?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_CONNECT_CRYPTOX}>
                                    <div data-scrollable="true">
                                        <div>
                                            <StyledParagraph>{locale?.connectUseCryptoX?.paragraph}</StyledParagraph>
                                            <ul>
                                                <li>
                                                    <StyledParagraph>
                                                        {locale?.connectUseCryptoX?.onHeader}
                                                    </StyledParagraph>
                                                    {/* @TODO: Use Solana screenshot */}
                                                    <StyledImg
                                                        src="/faq/connect-wallet-1.webp"
                                                        alt="header wallet connect"
                                                    />
                                                </li>
                                                <li>
                                                    <Typography tag="p">
                                                        <StyledParagraph>
                                                            {locale?.connectUseCryptoX?.option}
                                                        </StyledParagraph>
                                                    </Typography>
                                                    <StyledImageContainer>
                                                        <StyledImg src="/faq/connect-wallet-2.webp" alt="header menu" />
                                                        <StyledImg
                                                            src="/faq/connect-wallet-3.webp"
                                                            alt="account settings"
                                                        />
                                                    </StyledImageContainer>
                                                </li>
                                            </ul>
                                            <Typography tag="p" semiBold>
                                                {locale?.connectUseCryptoX?.walletAddress}
                                            </Typography>
                                            <StyledImg src="/faq/wallet-list.webp" alt="wallet list" />
                                        </div>
                                    </div>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_CONNECT_METAMASK}>
                                    {locale?.connectUseMetaMask?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_CONNECT_METAMASK}>
                                    <div data-scrollable="true">
                                        <div>
                                            <ol>
                                                <li>
                                                    <StyledParagraph>
                                                        {locale?.connectUseMetaMask?.install}
                                                    </StyledParagraph>
                                                </li>
                                                <li>
                                                    <StyledParagraph>
                                                        {locale?.connectUseMetaMask?.createAccount}
                                                    </StyledParagraph>
                                                </li>
                                                <li>
                                                    <StyledParagraph>
                                                        {locale?.connectUseMetaMask?.connect}
                                                    </StyledParagraph>
                                                    <ul>
                                                        <li>
                                                            <StyledParagraph>
                                                                {locale?.connectUseMetaMask?.onHeader}
                                                            </StyledParagraph>
                                                            {/* @TODO: Use Solana screenshot */}
                                                            <StyledImg
                                                                src="/faq/connect-wallet-1.webp"
                                                                alt="header wallet connect"
                                                            />
                                                        </li>
                                                        <li>
                                                            <StyledParagraph>
                                                                {locale?.connectUseMetaMask?.option}
                                                            </StyledParagraph>
                                                            <StyledImageContainer>
                                                                <StyledImg
                                                                    src="/faq/connect-wallet-2.webp"
                                                                    alt="header menu"
                                                                />
                                                                <StyledImg
                                                                    src="/faq/connect-wallet-3.webp"
                                                                    alt="account settings"
                                                                />
                                                            </StyledImageContainer>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <StyledParagraph>
                                                        {locale?.connectUseMetaMask?.paragraph}
                                                    </StyledParagraph>
                                                    <StyledImg src="/faq/wallet-list.webp" alt="wallet list" />
                                                </li>
                                            </ol>
                                        </div>
                                    </div>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.CAN_VIEW_NFT_WALLET}>
                                    {locale?.canIViewNFTOnMyWallet?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.CAN_VIEW_NFT_WALLET}>
                                    {locale?.canIViewNFTOnMyWallet?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                    <StyledListStep>
                                        {locale?.canIViewNFTOnMyWallet?.guideSteps?.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </StyledListStep>
                                    <br />
                                    <Typography tag="p" semiBold>
                                        {locale?.canIViewNFTOnMyWallet?.metaMask}
                                    </Typography>
                                    <Typography tag="p">
                                        {locale?.canIViewNFTOnMyWallet?.follow}
                                        <a
                                            href="https://metamask.zendesk.com/hc/en-us/articles/360058238591-NFT-tokens-in-your-MetaMask-wallet"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {locale?.canIViewNFTOnMyWallet?.instructions}
                                        </a>
                                    </Typography>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                        </Accordion>
                    </FlexBox>
                </StyledFaqPageContent>
            </PageLayout>
        </div>
    );
};